import React, { useMemo, useState } from 'react';
import { Plus, X, Check, AlertTriangle, Building2, MapPin, Network } from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { UserRole } from '../../types';
import {
  branchesOfBankZone,
  isPlaceholderName,
  requirementsFor,
  sameName,
  zonesOfBank,
} from '../../utils/hierarchy';

/**
 * Bank -> Zonal Office -> Branch, in that order, each list filtered by the
 * choice above it, with a "+ Create" beside each one.
 *
 * Two things this fixes that were invisible before:
 *
 *  - The zone list showed every zone in the database whatever bank was
 *    picked, so a user could be given a zone belonging to another bank.
 *  - Changing the bank left the previously ticked branches in place, so the
 *    saved user held branches of a bank they do not work for.
 *
 * The "+ Create" panels are plain divs, not <form> elements. This component
 * is used INSIDE the Create User form, and a nested form would submit the
 * outer one -- creating the user the moment you tried to add a branch.
 */

export interface HierarchyPickerProps {
  role: UserRole;
  banks: string[];
  onBanksChange: (next: string[]) => void;
  zone: string;
  onZoneChange: (next: string) => void;
  branches: string[];
  onBranchesChange: (next: string[]) => void;
  /** Hidden for roles that need no branch, so the form matches the rule. */
  disabled?: boolean;
}

type Panel = 'none' | 'bank' | 'zone' | 'branch';

export const HierarchyPicker: React.FC<HierarchyPickerProps> = ({
  role,
  banks: selectedBanks,
  onBanksChange,
  zone,
  onZoneChange,
  branches: selectedBranches,
  onBranchesChange,
  disabled,
}) => {
  const {
    banks,
    zones,
    branches,
    addBank,
    addZone,
    addBranch,
  } = useSRMS();

  const need = requirementsFor(role);
  const [panel, setPanel] = useState<Panel>('none');

  // New-item fields. Kept in this component so opening a panel and cancelling
  // never touches what has already been typed into the user form.
  const [nbName, setNbName] = useState('');
  const [nbCode, setNbCode] = useState('');
  const [nbRate, setNbRate] = useState('10');

  const [nzName, setNzName] = useState('');
  const [nzHead, setNzHead] = useState('');

  const [nBrName, setNBrName] = useState('');
  const [nBrCode, setNBrCode] = useState('');
  const [nBrManager, setNBrManager] = useState('');

  const [panelError, setPanelError] = useState('');

  const primaryBank = selectedBanks[0] || '';

  /** Real banks only. "All Banks" and friends are not banks. */
  const bankOptions = useMemo(
    () =>
      Array.from(new Set(banks.map((b) => (b.name || '').trim()).filter((n) => !isPlaceholderName(n))))
        .sort(),
    [banks]
  );

  const zoneOptions = useMemo(() => {
    if (!primaryBank) return [];
    return Array.from(
      new Set(
        zonesOfBank(zones, primaryBank)
          .map((z) => (z.name || '').trim())
          .filter((n) => !isPlaceholderName(n))
      )
    ).sort();
  }, [zones, primaryBank]);

  const branchOptions = useMemo(() => {
    if (!primaryBank) return [];
    return Array.from(
      new Set(
        branchesOfBankZone(branches, primaryBank, zone || undefined, banks)
          .map((b) => (b.name || '').trim())
          .filter((n) => !isPlaceholderName(n))
      )
    ).sort();
  }, [branches, primaryBank, zone]);

  /* ---------------- cascade: a change upstream clears downstream ---------------- */

  const toggleBank = (name: string, on: boolean) => {
    const next = on
      ? [...selectedBanks, name]
      : selectedBanks.filter((b) => !sameName(b, name));
    onBanksChange(next);

    // The primary bank decides the zone and branch lists. If it changed, the
    // zone and branches below it are no longer valid -- keeping them is how a
    // user ended up holding another bank's branch.
    const nextPrimary = next[0] || '';
    if (!sameName(nextPrimary, primaryBank)) {
      onZoneChange('');
      onBranchesChange([]);
    } else if (!on) {
      // A secondary bank was unticked: drop only its branches.
      onBranchesChange(
        selectedBranches.filter((brName) => {
          const br = branches.find((b) => sameName(b.name, brName));
          if (!br || isPlaceholderName(br.bankName)) return true;
          return next.some((b) => sameName(b, br.bankName));
        })
      );
    }
  };

  const changeZone = (name: string) => {
    onZoneChange(name);
    // Branches belong to bank + zone. A different zone means a different list.
    onBranchesChange([]);
  };

  const toggleBranch = (name: string, on: boolean) => {
    if (need.branch === 'one') {
      // A Branch Manager holds exactly one branch, so ticking replaces.
      onBranchesChange(on ? [name] : []);
      return;
    }
    onBranchesChange(
      on ? [...selectedBranches, name] : selectedBranches.filter((b) => !sameName(b, name))
    );
  };

  /* ---------------- + Create handlers ---------------- */

  const openPanel = (p: Panel) => {
    setPanelError('');
    setPanel(p);
  };

  const closePanel = () => {
    setPanel('none');
    setPanelError('');
  };

  const saveBank = () => {
    const name = nbName.trim();
    if (!name) return setPanelError('Enter the bank name.');
    if (bankOptions.some((b) => sameName(b, name))) {
      return setPanelError(`"${name}" is already in the list.`);
    }
    const created = addBank({
      name,
      code: nbCode.trim().toUpperCase() || 'BNK',
      defaultCommissionRate: Number(nbRate) > 0 ? Number(nbRate) : 10,
      active: true,
    });
    // Selected straight away, and it becomes the primary if it is the first.
    onBanksChange([...selectedBanks, created.name]);
    if (selectedBanks.length === 0) {
      onZoneChange('');
      onBranchesChange([]);
    }
    setNbName('');
    setNbCode('');
    setNbRate('10');
    closePanel();
  };

  const saveZone = () => {
    const name = nzName.trim();
    if (!primaryBank) return setPanelError('Select a bank first — a zone belongs to one bank.');
    if (!name) return setPanelError('Enter the zonal office name.');
    if (zoneOptions.some((z) => sameName(z, name))) {
      return setPanelError(`"${name}" already exists under ${primaryBank}.`);
    }
    const created = addZone({
      name,
      headName: nzHead.trim() || 'Zonal Head',
      bankName: primaryBank,
      active: true,
    });
    changeZone(created.name);
    setNzName('');
    setNzHead('');
    closePanel();
  };

  const saveBranch = () => {
    const name = nBrName.trim();
    if (!primaryBank) return setPanelError('Select a bank first.');
    if (need.zone && !zone) return setPanelError('Select a zonal office first.');
    if (!name) return setPanelError('Enter the branch name.');
    if (branchOptions.some((b) => sameName(b, name))) {
      return setPanelError(`"${name}" already exists under ${zone || primaryBank}.`);
    }
    const created = addBranch({
      name,
      code: nBrCode.trim().toUpperCase() || 'BR',
      bankName: primaryBank,
      zoneName: zone,
      zone,
      managerName: nBrManager.trim() || 'Branch Manager',
    });
    toggleBranch(created.name, true);
    setNBrName('');
    setNBrCode('');
    setNBrManager('');
    closePanel();
  };

  /* ---------------- render ---------------- */

  const needsAnything = need.bank || need.zone || need.branch !== false;
  if (!needsAnything) {
    return (
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-[11px] text-slate-600">
        {need.note}
      </div>
    );
  }

  const addBtn = 'inline-flex items-center gap-1 px-2.5 py-1.5 bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-lg font-bold text-[11px] transition shrink-0';
  const fieldCls =
    'w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400';

  return (
    <div className="p-3 bg-blue-50/60 border border-blue-100 rounded-xl space-y-3 text-xs">
      <div className="flex items-center gap-2 text-blue-900 font-bold">
        <Network className="w-4 h-4" />
        <span>Bank → Zonal Office → Branch</span>
      </div>
      <p className="text-[11px] text-slate-600 leading-relaxed">{need.note}</p>

      {/* ---------------- BANK ---------------- */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-1">
          <label className="text-slate-700 font-bold">
            Bank {need.bank && <span className="text-rose-600">*</span>}
            <span className="ml-1 font-medium text-slate-500">
              (tick every bank this user works for)
            </span>
          </label>
          <button type="button" onClick={() => openPanel('bank')} className={addBtn}>
            <Plus className="w-3 h-3" />
            <span>Create Bank</span>
          </button>
        </div>
        <div className="max-h-24 overflow-y-auto bg-white border border-slate-200 rounded-xl p-2 space-y-1">
          {bankOptions.length === 0 ? (
            <span className="text-slate-400">
              No banks yet — use “+ Create Bank”.
            </span>
          ) : (
            bankOptions.map((b) => (
              <label key={`hp-bank-${b}`} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={selectedBanks.some((x) => sameName(x, b))}
                  onChange={(e) => toggleBank(b, e.target.checked)}
                />
                <span className="text-slate-800 font-medium">{b}</span>
                {sameName(selectedBanks[0], b) && (
                  <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-[9px] font-bold">
                    PRIMARY
                  </span>
                )}
              </label>
            ))
          )}
        </div>
      </div>

      {/* ---------------- ZONE ---------------- */}
      {need.zone && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <label className="text-slate-700 font-bold">
              Zonal Office <span className="text-rose-600">*</span>
            </label>
            <button
              type="button"
              onClick={() => openPanel('zone')}
              disabled={!primaryBank}
              className={`${addBtn} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <Plus className="w-3 h-3" />
              <span>Create Zonal Office</span>
            </button>
          </div>
          <select
            value={zone}
            disabled={disabled || !primaryBank}
            onChange={(e) => changeZone(e.target.value)}
            className={fieldCls}
          >
            <option value="">
              {primaryBank ? 'Select zonal office…' : 'Select a bank first'}
            </option>
            {zoneOptions.map((z) => (
              <option key={`hp-zone-${z}`} value={z}>
                {z}
              </option>
            ))}
          </select>
          {primaryBank && zoneOptions.length === 0 && (
            <p className="mt-1 text-[11px] text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              <span>
                {primaryBank} has no zonal office yet. Create one — a user cannot be
                created without it.
              </span>
            </p>
          )}
        </div>
      )}

      {/* ---------------- BRANCH ---------------- */}
      {need.branch !== false && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <label className="text-slate-700 font-bold">
              Branch{need.branch === 'many' ? 'es' : ''}{' '}
              <span className="text-rose-600">*</span>
              <span className="ml-1 font-medium text-slate-500">
                {need.branch === 'one' ? '(exactly one)' : '(tick every branch covered)'}
              </span>
            </label>
            <button
              type="button"
              onClick={() => openPanel('branch')}
              disabled={!primaryBank || (need.zone && !zone)}
              className={`${addBtn} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <Plus className="w-3 h-3" />
              <span>Create Branch</span>
            </button>
          </div>
          <div className="max-h-28 overflow-y-auto bg-white border border-slate-200 rounded-xl p-2 space-y-1">
            {!primaryBank ? (
              <span className="text-slate-400">Select a bank first.</span>
            ) : need.zone && !zone ? (
              <span className="text-slate-400">Select a zonal office first.</span>
            ) : branchOptions.length === 0 ? (
              <span className="text-slate-400">
                No branches under {zone || primaryBank} yet — use “+ Create Branch”.
              </span>
            ) : (
              branchOptions.map((br) => (
                <label key={`hp-br-${br}`} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type={need.branch === 'one' ? 'radio' : 'checkbox'}
                    name={need.branch === 'one' ? 'hp-single-branch' : undefined}
                    disabled={disabled}
                    checked={selectedBranches.some((x) => sameName(x, br))}
                    onChange={(e) => toggleBranch(br, e.target.checked)}
                  />
                  <span className="text-slate-800 font-medium">{br}</span>
                </label>
              ))
            )}
          </div>
          {selectedBranches.length > 0 && (
            <p className="mt-1 text-[11px] text-slate-500">
              Selected: {selectedBranches.join(', ')}
            </p>
          )}
        </div>
      )}

      {/* ---------------- + CREATE PANELS ---------------- */}
      {panel !== 'none' && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full border border-slate-200 overflow-y-auto max-h-[92vh]">
            <div className="px-5 py-3 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-bold">
                {panel === 'bank' && <Building2 className="w-4 h-4 text-blue-400" />}
                {panel === 'zone' && <MapPin className="w-4 h-4 text-blue-400" />}
                {panel === 'branch' && <Network className="w-4 h-4 text-blue-400" />}
                <span>
                  {panel === 'bank'
                    ? 'New Bank'
                    : panel === 'zone'
                    ? 'New Zonal Office'
                    : 'New Branch'}
                </span>
              </div>
              <button
                type="button"
                onClick={closePanel}
                className="text-slate-400 hover:text-white p-1 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-3 text-xs">
              <p className="text-[11px] text-slate-500">
                Nothing you have already typed into the user form is lost — this
                closes and returns you to it with the new item selected.
              </p>

              {panel === 'bank' && (
                <>
                  <div>
                    <label className="block text-slate-700 font-bold mb-1">Bank name *</label>
                    <input
                      autoFocus
                      value={nbName}
                      onChange={(e) => setNbName(e.target.value)}
                      placeholder="e.g. Bank of Maharashtra"
                      className={fieldCls}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-700 font-bold mb-1">Code</label>
                      <input
                        value={nbCode}
                        onChange={(e) => setNbCode(e.target.value)}
                        placeholder="BOM"
                        className={`${fieldCls} font-mono`}
                      />
                    </div>
                    <div>
                      <label className="block text-slate-700 font-bold mb-1">
                        Commission %
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={nbRate}
                        onChange={(e) => setNbRate(e.target.value)}
                        className={`${fieldCls} font-mono font-bold`}
                      />
                    </div>
                  </div>
                </>
              )}

              {panel === 'zone' && (
                <>
                  <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[11px] text-slate-600">
                    Will be created under <strong>{primaryBank}</strong>.
                  </div>
                  <div>
                    <label className="block text-slate-700 font-bold mb-1">
                      Zonal office name *
                    </label>
                    <input
                      autoFocus
                      value={nzName}
                      onChange={(e) => setNzName(e.target.value)}
                      placeholder="e.g. Chhatrapati Sambhajinagar Zone"
                      className={fieldCls}
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-bold mb-1">Zonal head</label>
                    <input
                      value={nzHead}
                      onChange={(e) => setNzHead(e.target.value)}
                      placeholder="Optional"
                      className={fieldCls}
                    />
                  </div>
                </>
              )}

              {panel === 'branch' && (
                <>
                  <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[11px] text-slate-600">
                    Will be created under <strong>{primaryBank}</strong>
                    {zone ? (
                      <>
                        {' '}
                        → <strong>{zone}</strong>
                      </>
                    ) : null}
                    .
                  </div>
                  <div>
                    <label className="block text-slate-700 font-bold mb-1">Branch name *</label>
                    <input
                      autoFocus
                      value={nBrName}
                      onChange={(e) => setNBrName(e.target.value)}
                      placeholder="e.g. Karmad"
                      className={fieldCls}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-700 font-bold mb-1">Code</label>
                      <input
                        value={nBrCode}
                        onChange={(e) => setNBrCode(e.target.value)}
                        placeholder="KRM"
                        className={`${fieldCls} font-mono`}
                      />
                    </div>
                    <div>
                      <label className="block text-slate-700 font-bold mb-1">Manager</label>
                      <input
                        value={nBrManager}
                        onChange={(e) => setNBrManager(e.target.value)}
                        placeholder="Optional"
                        className={fieldCls}
                      />
                    </div>
                  </div>
                </>
              )}

              {panelError && (
                <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <span>{panelError}</span>
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={closePanel}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={
                    panel === 'bank' ? saveBank : panel === 'zone' ? saveZone : saveBranch
                  }
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-sm transition"
                >
                  <Check className="w-4 h-4" />
                  <span>Save &amp; select</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchyPicker;
